import flatpickr from 'flatpickr';
import { Polish as PolishBase } from 'flatpickr/dist/esm/l10n/pl.js';
import confirmDatePlugin from 'flatpickr/dist/esm/plugins/confirmDate/confirmDate.js';
import 'flatpickr/dist/flatpickr.min.css';
import 'flatpickr/dist/plugins/confirmDate/confirmDate.css';
import '../scss/BirthDatePicker.scss';

/** Prod-like weekday labels (po / wt / śr…). */
const Polish = {
  ...PolishBase,
  weekdays: {
    ...PolishBase.weekdays,
    shorthand: ['nd', 'po', 'wt', 'śr', 'cz', 'pt', 'so'],
  },
};

const toPolish = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
};

const parsePolishDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d
      ? date
      : null;
  }
  const parts = raw.split('.');
  if (parts.length !== 3 || parts[2].length !== 4) return null;
  const day = Number(parts[0]);
  const month = Number(parts[1]);
  const year = Number(parts[2]);
  if (!day || !month || !year) return null;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : null;
};

/**
 * Rok jak miesiąc: natywna lista, nie pole numeryczne z +/-.
 * Flatpickr trzyma rok w `yearElements` i ustawia `.value` przy zmianie miesiąca.
 */
const mountYearDropdown = (instance) => {
  const input = instance.calendarContainer?.querySelector('input.cur-year');
  if (!input) return;

  const maxYear = instance.config.maxDate
    ? instance.config.maxDate.getFullYear()
    : new Date().getFullYear();
  const minYear = instance.config.minDate
    ? instance.config.minDate.getFullYear()
    : maxYear - 110;

  const select = document.createElement('select');
  select.className = 'flatpickr-yearDropdown cur-year';
  select.setAttribute('aria-label', instance.l10n?.yearAriaLabel || 'Rok');

  for (let year = maxYear; year >= minYear; year -= 1) {
    const option = document.createElement('option');
    option.value = String(year);
    option.textContent = String(year);
    select.appendChild(option);
  }

  const current = String(instance.currentYear);
  select.value = [...select.options].some((option) => option.value === current)
    ? current
    : String(maxYear);

  const wrapper = input.closest('.numInputWrapper');
  wrapper?.querySelectorAll('.arrowUp, .arrowDown').forEach((arrow) => arrow.remove());
  input.replaceWith(select);
  if (wrapper) wrapper.style.width = 'auto';

  const index = instance.yearElements?.indexOf(input) ?? -1;
  if (index >= 0) instance.yearElements[index] = select;
  else instance.yearElements = [select];

  select.addEventListener('change', () => {
    const year = Number(select.value);
    if (!Number.isFinite(year) || year === instance.currentYear) return;
    instance.changeYear(year);
  });
};

/**
 * Flatpickr liczy wysokość jako sumę dzieci (bez paddingu kontenera),
 * więc przy flipie „above” kalendarz ląduje za nisko. Przeliczamy z offsetHeight.
 */
const repositionCalendar = (instance) => {
  const cal = instance?.calendarContainer;
  const anchor = instance?._positionElement || instance?._input;
  if (!cal || !anchor || instance.config?.inline || instance.config?.static) return;

  const inputBounds = anchor.getBoundingClientRect();
  const calendarHeight = cal.offsetHeight;
  const calendarWidth = cal.offsetWidth;
  const gap = 4;
  const spaceBelow = window.innerHeight - inputBounds.bottom;
  const showOnTop =
    spaceBelow < calendarHeight + gap && inputBounds.top > calendarHeight + gap;

  const top =
    window.pageYOffset +
    inputBounds.top +
    (showOnTop ? -calendarHeight - gap : anchor.offsetHeight + gap);

  cal.classList.toggle('arrowTop', !showOnTop);
  cal.classList.toggle('arrowBottom', showOnTop);
  cal.style.top = `${top}px`;

  let left = window.pageXOffset + inputBounds.left;
  const rightMost = left + calendarWidth > document.body.offsetWidth;
  if (!rightMost) {
    cal.style.left = `${left}px`;
    cal.style.right = 'auto';
    return;
  }

  const right = document.body.offsetWidth - (window.pageXOffset + inputBounds.right);
  cal.style.left = 'auto';
  cal.style.right = `${right}px`;
};

/**
 * Flatpickr birth-date picker (PL locale, „Wybierz”, max=today).
 * Mirrors prod Vue Datepicker UX on travel booking.
 */
export const attachBirthDatePicker = (input, options = {}) => {
  if (!input) return null;

  const {
    minDate = null,
    onChange = null,
    autocomplete = 'bday',
    allowInput = true,
    confirm = true,
  } = options;
  const maxDate = Object.prototype.hasOwnProperty.call(options, 'maxDate')
    ? options.maxDate
    : new Date();

  const existing = input._birthDatePicker;
  if (existing?.destroy) existing.destroy();

  input.setAttribute('placeholder', options.placeholder || 'DD.MM.RRRR');
  input.setAttribute('autocomplete', autocomplete || 'off');
  if (allowInput) {
    input.setAttribute('inputmode', 'numeric');
    input.removeAttribute('readonly');
  } else {
    input.setAttribute('readonly', 'readonly');
    input.removeAttribute('inputmode');
  }
  input.classList.add('js-birth-datepicker');

  const fp = flatpickr(input, {
    locale: Polish,
    dateFormat: 'd.m.Y',
    allowInput,
    disableMobile: true,
    clickOpens: true,
    closeOnSelect: !confirm,
    maxDate: maxDate || undefined,
    minDate: minDate || undefined,
    monthSelectorType: 'dropdown',
    position: 'auto',
    appendTo: document.body,
    nextArrow: '›',
    prevArrow: '‹',
    plugins: confirm
      ? [
        confirmDatePlugin({
          confirmText: 'Wybierz',
          confirmIcon: '',
          showAlways: true,
          theme: 'light',
        }),
      ]
      : [],
    onChange: (selectedDates, dateStr) => {
      if (typeof onChange === 'function') onChange(selectedDates[0] || null, dateStr);
    },
    onReady: (_dates, _str, instance) => {
      instance.calendarContainer.classList.add('hski-datepicker');
      mountYearDropdown(instance);
      const confirmBtn = instance.calendarContainer.querySelector('.flatpickr-confirm');
      if (confirmBtn) {
        confirmBtn.classList.add('hski-datepicker__confirm');
        confirmBtn.textContent = 'Wybierz';
      }
    },
    onOpen: (_dates, _str, instance) => {
      // Po layoutcie (padding + „Wybierz”) — flip above/below z realną wysokością.
      requestAnimationFrame(() => repositionCalendar(instance));
    },
  });

  const icon = input.closest('.booking-form__control--date')
    ?.querySelector('.booking-form__control-icon, .js-birth-datepicker-icon');
  if (icon && !icon.dataset.fpBound) {
    icon.dataset.fpBound = '1';
    icon.style.cursor = 'pointer';
    // Zawsze bieżąca instancja flatpickr (po re-attach stary `fp` jest zniszczony).
    icon.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const live = input._birthDatePicker?.fp;
      if (!live || !live.input || live.config?.inline) return;
      try {
        live.open();
      } catch {
        /* destroyed / detached */
      }
    });
  }

  const api = {
    fp,
    get value() {
      return input.value?.trim() || '';
    },
    set value(next) {
      const date = next instanceof Date ? next : parsePolishDate(next);
      if (date) {
        fp.setDate(date, false);
        input.value = toPolish(date);
        return;
      }
      fp.clear(false);
      input.value = '';
    },
    get date() {
      return fp.selectedDates[0] || parsePolishDate(input.value);
    },
    get isComplete() {
      return Boolean(parsePolishDate(input.value));
    },
    open: () => fp.open(),
    close: () => fp.close(),
    destroy: () => {
      try {
        fp.destroy();
      } catch {
        /* already destroyed */
      }
      delete input._birthDatePicker;
    },
  };

  input._birthDatePicker = api;
  return api;
};

export const destroyBirthDatePicker = (pickerOrInput) => {
  if (!pickerOrInput) return;
  if (typeof pickerOrInput.destroy === 'function') {
    pickerOrInput.destroy();
    return;
  }
  pickerOrInput._birthDatePicker?.destroy?.();
};

export const validateBirthDatePicker = (pickerOrInput, options = {}) => {
  const {
    required = true,
    maxDate = new Date(),
    minDate = null,
    minAgeYears = null,
    fieldLabel = 'Data urodzenia',
  } = options;

  const picker = pickerOrInput?._birthDatePicker || pickerOrInput;
  const value = picker?.value ?? pickerOrInput?.value?.trim?.() ?? '';

  if (!value) {
    return required
      ? { valid: false, message: `${fieldLabel}: uzupełnij datę w formacie DD.MM.RRRR` }
      : { valid: true, message: '' };
  }

  const date = parsePolishDate(value);
  if (!date) {
    return { valid: false, message: `${fieldLabel}: podaj poprawną datę` };
  }

  const max = maxDate instanceof Date ? maxDate : null;
  if (max) {
    const ceiling = new Date(max);
    ceiling.setHours(23, 59, 59, 999);
    if (date > ceiling) {
      return { valid: false, message: `${fieldLabel}: data nie może być z przyszłości` };
    }
  }

  if (minDate instanceof Date) {
    const floor = new Date(minDate);
    floor.setHours(0, 0, 0, 0);
    if (date < floor) {
      return {
        valid: false,
        message: options.minDateMessage || `${fieldLabel}: data jest zbyt wczesna`,
      };
    }
  }

  if (Number.isFinite(Number(minAgeYears)) && Number(minAgeYears) > 0) {
    const limit = new Date();
    limit.setHours(0, 0, 0, 0);
    limit.setFullYear(limit.getFullYear() - Number(minAgeYears));
    if (date > limit) {
      return {
        valid: false,
        message: options.minAgeMessage
          || `Musisz mieć ukończone ${Number(minAgeYears)} lat`,
      };
    }
  }

  return { valid: true, message: '', date };
};

export { parsePolishDate, toPolish };
